import type { FrontendLogMeta } from "@/utils/logging/IFrontendLogger";

type ApiMeta = {
    endpoint: string;
    method: string;
    feature: string;
    operation: string;
    status?: number;
    durationMs?: number;
    correlationId?: string;
};

type BaseQueryRequestLogger = {
    debug: (message: string, meta?: FrontendLogMeta) => void;
    error: (message: string, error?: Error, meta?: FrontendLogMeta) => void;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const isUnsafeMetaKey = (key: string): boolean =>
    /(authorization|x-?auth|token|password|cookie|email|phone)/i.test(key);

const safeMeta = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value.map(item => safeMeta(item));
    }

    if (!isRecord(value)) {
        return value;
    }

    const redacted: Record<string, unknown> = {};
    Object.entries(value).forEach(([key, itemValue]) => {
        if (isUnsafeMetaKey(key)) {
            redacted[key] = "[REDACTED]";
            return;
        }

        redacted[key] = safeMeta(itemValue);
    });

    return redacted;
};

const asFrontendLogMeta = (value: unknown): FrontendLogMeta => {
    return value as FrontendLogMeta;
};

const buildFailureMeta = (args: {
    endpoint: string;
    method: string;
    durationMs: number;
    status: number;
    correlationId: string;
}): ApiMeta => ({
    feature: "FND",
    operation: "API_REQUEST_FAIL",
    endpoint: args.endpoint,
    method: args.method,
    status: args.status,
    durationMs: args.durationMs,
    correlationId: args.correlationId,
});

const buildSuccessMeta = (args: {
    endpoint: string;
    method: string;
    status: number;
    durationMs: number;
    correlationId: string;
}): ApiMeta => ({
    feature: "FND",
    operation: "API_REQUEST_SUCCESS",
    endpoint: args.endpoint,
    method: args.method,
    status: args.status,
    durationMs: args.durationMs,
    correlationId: args.correlationId,
});

const buildAbortMeta = (args: {
    endpoint: string;
    method: string;
    durationMs: number;
    correlationId: string;
}): ApiMeta => ({
    feature: "FND",
    operation: "API_REQUEST_ABORTED",
    endpoint: args.endpoint,
    method: args.method,
    status: 499,
    durationMs: args.durationMs,
    correlationId: args.correlationId,
});

const buildStartMeta = (args: {
    endpoint: string;
    method: string;
    correlationId: string;
}): ApiMeta => ({
    feature: "FND",
    operation: "API_REQUEST_START",
    endpoint: args.endpoint,
    method: args.method,
    correlationId: args.correlationId,
});

export const logOperationStart = (
    requestLogger: BaseQueryRequestLogger,
    args: {
        endpoint: string;
        method: string;
        correlationId: string;
    },
): void => {
    requestLogger.debug(
        `API request start: ${args.method} ${args.endpoint}`,
        asFrontendLogMeta(safeMeta(buildStartMeta(args))),
    );
};

export const logOperationSuccess = (
    requestLogger: BaseQueryRequestLogger,
    args: {
        endpoint: string;
        method: string;
        status: number;
        durationMs: number;
        correlationId: string;
    },
): void => {
    requestLogger.debug(
        `API request succeeded: ${args.method} ${args.endpoint}`,
        asFrontendLogMeta(safeMeta(buildSuccessMeta(args))),
    );
};

export const logOperationAbort = (
    requestLogger: BaseQueryRequestLogger,
    args: {
        endpoint: string;
        method: string;
        durationMs: number;
        correlationId: string;
        extraMeta?: Record<string, unknown>;
    },
): void => {
    const baseMeta = buildAbortMeta({
        endpoint: args.endpoint,
        method: args.method,
        durationMs: args.durationMs,
        correlationId: args.correlationId,
    });

    requestLogger.debug(
        `API request aborted: ${args.method} ${args.endpoint}`,
        asFrontendLogMeta(
            safeMeta({
                ...baseMeta,
                ...(args.extraMeta ?? {}),
            }),
        ),
    );
};

export const logOperationFail = (
    requestLogger: BaseQueryRequestLogger,
    args: {
        endpoint: string;
        method: string;
        error: Error;
        durationMs: number;
        status: number;
        correlationId: string;
        extraMeta?: Record<string, unknown>;
    },
): void => {
    const baseMeta = buildFailureMeta({
        endpoint: args.endpoint,
        method: args.method,
        durationMs: args.durationMs,
        status: args.status,
        correlationId: args.correlationId,
    });

    requestLogger.error(
        `API request failed: ${args.method} ${args.endpoint}`,
        args.error,
        safeMeta({
            ...baseMeta,
            ...(args.extraMeta ?? {}),
        }) as FrontendLogMeta,
    );
};
